import React, { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  SortingState,
  PaginationState,
  ColumnDef,
  CellContext,
} from '@tanstack/react-table';
import * as Styled from './DSOTable.styles';
import { DSOTableProps, DSOTableColumn, TableRow } from './DSOTable.types';
import { DsoLoadingSpinner } from 'components/core';

/**
 * DSOTable Component with TanStack Table integration
 *
 * Features:
 * - Sorting (client-side or server-side)
 * - Pagination (client-side or server-side)
 * - Custom cell rendering
 * - Row click handlers
 * - Loading states
 * - Backward compatible with legacy column definitions
 */
const DSOTable: React.FC<DSOTableProps> = ({
  columns,
  data,
  onRowClick,
  noDataMessage = 'No data available',
  config = {},
  onPaginationChange,
  pageCount,
  onSortingChange,
  initialSorting = [],
  onRowSelectionChange,
  isLoading = false,
  isFetchingMore = false,
  infiniteScrollRef,
}) => {
  // Default configuration
  const {
    enableSorting = true,
    enablePagination = false,
    enableRowSelection = false,
    manualSorting = false,
    manualPagination = false,
    pageSize = 10,
    pageSizeOptions = [10, 20, 30, 50],
  } = config;

  // State management
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  });
  const [rowSelection, setRowSelection] = useState({});

  // Convert legacy column definitions to TanStack format
  const tanstackColumns = useMemo<ColumnDef<TableRow>[]>(() => {
    return columns.map((col: DSOTableColumn) => ({
      id: col.key,
      accessorKey: col.key,
      header: col.label,
      enableSorting: col.enableSorting ?? enableSorting,
      sortDescFirst: col.sortDescFirst,
      size: col.width,
      cell: (info: CellContext<TableRow, unknown>) => {
        const value = info.getValue();
        const row = info.row.original;

        if (col.render) {
          return col.render(value, row);
        }
        return value;
      },
    }));
  }, [columns, enableSorting]);

  // Initialize TanStack Table
  const table = useReactTable({
    data,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel: enablePagination
      ? getPaginationRowModel()
      : undefined,

    // Sorting
    manualSorting,
    enableSorting,
    onSortingChange: updater => {
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
      if (onSortingChange) {
        onSortingChange(newSorting);
      }
    },
    state: {
      sorting,
      pagination,
      rowSelection,
    },

    // Pagination
    manualPagination,
    pageCount: pageCount,
    onPaginationChange: updater => {
      const newPagination =
        typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(newPagination);
      if (onPaginationChange) {
        onPaginationChange(newPagination);
      }
    },

    // Row selection
    enableRowSelection,
    onRowSelectionChange: updater => {
      const newSelection =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(newSelection);
      if (onRowSelectionChange) {
        const selectedRows = table
          .getSelectedRowModel()
          .rows.map(row => row.original);
        onRowSelectionChange(selectedRows);
      }
    },
  });

  const handleRowClick = (row: TableRow) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  return (
    <Styled.TableContainer>
      <Styled.TableWrapper>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <Styled.TableHeader
                  key={header.id}
                  width={header.column.getSize()}
                  onClick={
                    header.column.getCanSort()
                      ? header.column.getToggleSortingHandler()
                      : undefined
                  }
                  style={{
                    cursor: header.column.getCanSort() ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getCanSort() && (
                      <Styled.SortingIcons>
                        {{
                          asc: ' ↑',
                          desc: ' ↓',
                        }[header.column.getIsSorted() as string] ?? ' ⇅'}
                      </Styled.SortingIcons>
                    )}
                  </div>
                </Styled.TableHeader>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {/* Initial load — no rows exist yet */}
          {isLoading && table.getRowModel().rows.length === 0 && (
            <Styled.NoDataContainer>
              <td colSpan={columns.length}>
                <DsoLoadingSpinner />
              </td>
            </Styled.NoDataContainer>
          )}

          {/* Empty state — finished loading but no data */}
          {!isLoading && table.getRowModel().rows.length === 0 && (
            <Styled.NoDataContainer>
              <td colSpan={columns.length}>{noDataMessage}</td>
            </Styled.NoDataContainer>
          )}

          {/* Data rows — always rendered when rows exist, regardless of isFetchingMore */}
          {table.getRowModel().rows.map(row => (
            <Styled.TableRow
              key={row.id}
              onClick={() => handleRowClick(row.original)}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
            >
              {row.getVisibleCells().map(cell => (
                <Styled.TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Styled.TableCell>
              ))}
            </Styled.TableRow>
          ))}

          {/* Fetching-more spinner — shown below existing rows while next page loads */}
          {isFetchingMore && (
            <tr>
              <td colSpan={columns.length}>
                <DsoLoadingSpinner />
              </td>
            </tr>
          )}

          {/* Sentinel row for infinite scroll — always last so IntersectionObserver fires correctly */}
          {infiniteScrollRef && (
            <tr>
              <td colSpan={columns.length}>
                <span ref={infiniteScrollRef} />
              </td>
            </tr>
          )}
        </tbody>
      </Styled.TableWrapper>

      {/* Pagination Controls — hidden when infinite scroll is active */}
      {enablePagination &&
        !infiniteScrollRef &&
        !isLoading &&
        table.getRowModel().rows.length > 0 && (
          <Styled.PaginationContainer>
            <Styled.PaginationControls>
              <Styled.PaginationButton
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                data-testid="pagination-first"
                aria-label="Go to first page"
              >
                {'<<'}
              </Styled.PaginationButton>
              <Styled.PaginationButton
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                data-testid="pagination-previous"
                aria-label="Go to previous page"
              >
                {'<'}
              </Styled.PaginationButton>

              <Styled.PageInfo data-testid="pagination-page-info">
                {table.getState().pagination.pageIndex + 1} /{' '}
                {table.getPageCount()}
              </Styled.PageInfo>

              <Styled.PaginationButton
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                data-testid="pagination-next"
                aria-label="Go to next page"
              >
                {'>'}
              </Styled.PaginationButton>
              <Styled.PaginationButton
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                data-testid="pagination-last"
                aria-label="Go to last page"
              >
                {'>>'}
              </Styled.PaginationButton>

              <Styled.PageSizeSelect
                value={table.getState().pagination.pageSize}
                onChange={e => {
                  table.setPageSize(Number(e.target.value));
                }}
                data-testid="pagination-page-size"
                aria-label="Select page size"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>
                    Show {size}
                  </option>
                ))}
              </Styled.PageSizeSelect>
            </Styled.PaginationControls>
          </Styled.PaginationContainer>
        )}
    </Styled.TableContainer>
  );
};

export default DSOTable;
